import React, { useState } from 'react';
import moment from 'moment/moment';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Icon,
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from '@unite-us/ui';
import { Filter } from '@unite-us/app-components';
import stateBgColor from 'common/utils/stateBgColor';
import { Spinner } from 'common/spinners';
import Pagination from 'src/common/Pagination/Pagination';
import EmptyTable from 'src/common/tables/EmptyTable/EmptyTable';
import ScrollToElement from 'src/common/utils/Form/ScrollToElement';
import {
  useFindDirectories,
} from 'src/components/Backoffice/api/hooks/prdApi/DirectoryHooks';
import { browserHistory } from 'common/utils/browserHistory';
import { tableWidth } from 'common/helpers/Tables';
import { theme } from 'src/../tailwind.config';
import { DEFAULT_PAGE_SIZE } from 'src/components/Backoffice/backofficeUtils';
import { connect } from 'react-redux';
import { hint2340AllowUsersToDeactivateOldPrds } from 'src/common/utils/FeatureFlags/flags';
import { getDomainUrl } from 'src/common/utils/Prd/domainUtils';

const prdDirectoryStatus = (prdDirectory) => {
  if (prdDirectory.current_configuration && !prdDirectory.has_draft_changes) {
    return 'published';
  } if (!prdDirectory.current_configuration) {
    return 'unpublished';
  }
  return 'draft changes';
};

const addPrdDirectory = () => {
  browserHistory.push({ pathname: '/backoffice/prds/new' });
};

const viewPrdDirectory = (prd) => () => {
  browserHistory.push({ pathname: `/backoffice/prds/${prd.id}` });
};

const ScrollToElementEvent = () => {
  ScrollToElement(document.getElementById('top-of-prd-table'));
};

const PrdTable = ({ isDeactivationEnabled }) => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);
  const [availabilityFilter, setAvailabilityFilter] = useState([]);

  const getDisabledFilter = () => {
    if (availabilityFilter.length === 1 && availabilityFilter.includes('Active')) return { disabled: false };
    if (availabilityFilter.length === 1 && availabilityFilter.includes('Inactive')) return { disabled: true };
    return {};
  };

  const filterOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
  ];

  const { data: directoriesResponse, isFetching } = useFindDirectories({
    page: {
      number: pageNumber,
      size: pageSize,
    },
    filter: getDisabledFilter(),
  });

  const { data: directories = [], paging = {} } = directoriesResponse || {};

  return (
    <>
      <div data-testid="prd-directories-header" id="top-of-prd-table" className="flex px-8 pt-12">
        <div className="w-9/12 flex flex-row relative">
          <h1 className="pt-1 pb-2 font-bold text-brand-blue">PRD Editor</h1>
        </div>
        <div className="w-3/12 flex justify-end">
          <Button
            id="add-form-button"
            iconLeft={<Icon color={theme.extend.colors['action-blue']} icon="V2Add" />}
            className={
              'py-0 px-4 h-12 text-sm text-action-blue rounded border solid border-dark-fill-blue bg-white normal-case'
            }
            label="Create a new PRD"
            onClick={() => addPrdDirectory()}
            data-testid="prd-create-button"
          />
        </div>
      </div>
      <div data-testid="prd-directories-table-container" style={{ padding: '30px' }}>
        {isDeactivationEnabled && (
          <Filter
            name="Availability"
            filterKey="availability"
            className="mb-5"
            hideSelectAll
            noMatchesPlaceholder=""
            selectedFilters={availabilityFilter}
            options={filterOptions}
            onFiltersChange={(activeFilters) => {
              setAvailabilityFilter(activeFilters);
              setPageNumber(1);
            }}
          />
        )}
        {isFetching ? <Spinner /> : (
          <>
            <Table id="prdDirectoriesTable">
              <TableHeader />
              <TableHeader>
                <TableRow>
                  <TableHeaderColumn style={{ width: tableWidth('medium') }}>
                    Customer Name
                  </TableHeaderColumn>
                  {isDeactivationEnabled && (
                    <TableHeaderColumn style={{ width: tableWidth('medium') }}>
                      URL
                    </TableHeaderColumn>
                  )}
                  <TableHeaderColumn style={{ width: tableWidth('small') }}>
                    Status
                  </TableHeaderColumn>
                  {isDeactivationEnabled && (
                    <TableHeaderColumn style={{ width: tableWidth('small') }}>
                      Availability
                    </TableHeaderColumn>
                  )}
                  <TableHeaderColumn style={{ width: tableWidth('small') }}>
                    Last Updated Date
                  </TableHeaderColumn>
                </TableRow>
              </TableHeader>
              <TableBody>
                {directories.map((prdDirectory) => {
                  const status = prdDirectoryStatus(prdDirectory);
                  const dataFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
                  const tableFormat = 'MMMM D, YYYY';
                  return (
                    <TableRow
                      key={`prd-directory-row-${prdDirectory.id}`}
                      onCellClick={viewPrdDirectory(prdDirectory)}
                    >
                      <TableRowColumn style={{ width: tableWidth('medium') }}>
                        {prdDirectory.customer_name}
                      </TableRowColumn>
                      {isDeactivationEnabled && (
                        <TableRowColumn style={{ width: tableWidth('medium') }}>
                          <a
                            href={`https://${getDomainUrl(prdDirectory.domain)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-action-blue hover:text-action-blue-dark underline"
                          >
                            {prdDirectory.domain}
                          </a>
                        </TableRowColumn>
                      )}
                      <TableRowColumn style={{ width: tableWidth('small') }}>
                        <div>
                          <Badge
                            text={status}
                            style={{ textTransform: 'capitalize' }}
                            color={stateBgColor(status)}
                          />
                        </div>
                      </TableRowColumn>
                      {isDeactivationEnabled && (
                        <TableRowColumn style={{ width: tableWidth('small') }}>
                          <div>
                            <Badge
                              text={prdDirectory.disabled ? 'Inactive' : 'Active'}
                              style={{ textTransform: 'capitalize' }}
                              color={stateBgColor(prdDirectory.disabled ? 'disabled' : 'enabled')}
                            />
                          </div>
                        </TableRowColumn>
                      )}
                      <TableRowColumn style={{ width: tableWidth('small') }}>
                        {(prdDirectory.updated_at && moment(prdDirectory.updated_at, dataFormat).format(tableFormat))}
                      </TableRowColumn>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {
              (paging.total_count === 0) &&
              (
                <EmptyTable
                  className={'p-6 border border-t-0 border-solid border-light-border-blue'}
                  message={'No Results Found'}
                />
              )
            }
            <div data-testid="prd-pagination-container" className="p-6">
              <Pagination
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalItemCount={paging.total_count}
                totalPageCount={paging.total_pages}
                setPageSize={setPageSize}
                setPageNumber={setPageNumber}
                scrollPaginationElement={ScrollToElementEvent}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

PrdTable.propTypes = {
  isDeactivationEnabled: PropTypes.bool,
};

PrdTable.defaultProps = {
  isDeactivationEnabled: false,
};

const mapStateToProps = (state) => ({
  isDeactivationEnabled: hint2340AllowUsersToDeactivateOldPrds(state),
});

export default connect(mapStateToProps)(PrdTable);
